import { useEffect, useState } from "react";
import machineLiveStatusApi from "../api/machineLiveStatusApi";

// =====================================================================
//  useMachineConnectedStatuses (DR-4) — ONE batch gateway-status fetch per
//  Machinery page load, shared by every "Midas" card row so the CONNECTED
//  (● live) state is sourced once, never per-card.
//
//  Returns the SET of machine ids that are live-connected. Fail-closed
//  (SEC-REQ-9): any error → empty set (every card renders "not connected").
//
//  `machineIds` is used ONLY as a stable dep key: while the on-page machine
//  set is unchanged the effect does not re-fire, and an EMPTY set (e.g. the
//  decommissioned tab — item 16) skips the fetch entirely. The fetch itself is
//  parameterless — the list endpoint returns the caller's whole connected set
//  and the caller intersects per card with `connectedIds.has(id)`.
//
//  NOTE (item 15): the previous `lastKeyRef` guard was removed. Under
//  React 18 StrictMode dev double-invoke it set the ref on the first run,
//  whose fetch was then cancelled by the first cleanup; the second run saw an
//  unchanged key and bailed out — so NO fetch ever completed and every card
//  read "not connected". The `[key]` dep already dedupes re-renders.
// =====================================================================

export function useMachineConnectedStatuses(machineIds: string[]): Set<string> {
  const [connectedIds, setConnectedIds] = useState<Set<string>>(new Set());

  // Stable key = sorted distinct ids. Only a real membership change re-fetches.
  const key = Array.from(new Set(machineIds.filter(Boolean))).sort().join(",");

  useEffect(() => {
    if (!key) {
      setConnectedIds(new Set());
      return;
    }

    let cancelled = false;
    machineLiveStatusApi
      .getConnectedIds()
      .then((set) => {
        if (!cancelled) setConnectedIds(set);
      })
      .catch(() => {
        if (!cancelled) setConnectedIds(new Set());
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  return connectedIds;
}

export default useMachineConnectedStatuses;
